import { apiGet, apiPut, apiClient } from './client';
import type {
  UserProfile,
  DeleteUserResponse,
  IdentitySpine,
  NotificationPreferences,
  QuestionnaireAnswers,
  UserPreferencesResponse,
} from '@/types/api';

/**
 * The PUT /user/profile body. Flat — the identity spine and the questionnaire
 * answers sit side by side at the top level — and every field is optional.
 */
export type UserProfileUpdate = IdentitySpine &
  QuestionnaireAnswers & {
    name?: string;
    preferences?: Record<string, unknown>;
  };

/**
 * Get current user's profile
 */
export async function getUserProfile(): Promise<UserProfile> {
  return apiGet<UserProfile>('/user/profile');
}

/**
 * Update user profile (name, preferences, identity spine, questionnaire answers)
 */
export async function updateUserProfile(updates: UserProfileUpdate): Promise<UserProfile> {
  return apiClient<UserProfile>('/user/profile', {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

/**
 * Permanently delete the authenticated user's account and all associated data.
 * Sends the required {"confirm":"DELETE"} body as a safeguard.
 */
export async function deleteUser(): Promise<DeleteUserResponse> {
  return apiClient<DeleteUserResponse>('/user/delete', {
    method: 'DELETE',
    body: JSON.stringify({ confirm: 'DELETE' }),
  });
}

/**
 * Read the user's email notification preferences.
 * Defaults to all-true if the user has no notificationPreferences map yet
 * (backend fills defaults via PREFERENCE_KEYS — no client-side defaulting needed).
 */
export async function getUserPreferences(): Promise<NotificationPreferences> {
  const res = await apiGet<UserPreferencesResponse>('/user/preferences');
  return res.preferences;
}

/**
 * Write the user's email notification preferences.
 * Must send ALL 5 keys; partial writes are rejected by the backend.
 */
export async function updateUserPreferences(
  prefs: NotificationPreferences
): Promise<NotificationPreferences> {
  const res = await apiPut<UserPreferencesResponse>('/user/preferences', prefs);
  return res.preferences;
}
